import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import confetti from "canvas-confetti";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ReadOnlyBanner } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PenLine, Clock, Flame, Snowflake, Paperclip, X, FileText, Mic, MicOff } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const localToday = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const MILESTONE_MSG = {
  7: "7-day streak! You're on fire.",
  30: "30 days strong — incredible consistency!",
  100: "100 days! You're a logging legend.",
};

const fireConfetti = () => {
  const end = Date.now() + 2000;
  const colors = ["#202123", "#f59e0b", "#202123", "#fbbf24"];
  (function frame() {
    confetti({ particleCount: 4, angle: 60, spread: 55, startVelocity: 55, origin: { x: 0 }, colors });
    confetti({ particleCount: 4, angle: 120, spread: 55, startVelocity: 55, origin: { x: 1 }, colors });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
};

export default function DailyLog() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const readOnly = user?.subscription_status === "read_only";
  const [form, setForm] = useState({ activity: "", date: today(), hours: "0", minutes: "0", linked_objective: "", notes: "" });
  const [todays, setTodays] = useState([]);
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // Voice-to-text via the browser's native Web Speech API (no third-party service).
  const SpeechRecognition = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
  const speechSupported = !!SpeechRecognition;
  const recognitionRef = useRef(null);
  const baseTextRef = useRef("");
  const finalRef = useRef("");        // accumulated finalized text across (re)starts
  const lastFinalRef = useRef("");    // last appended final segment (Android dedupe)
  const wantListeningRef = useRef(false);
  const [listening, setListening] = useState(false);
  const [voiceError, setVoiceError] = useState("");
  // Dictation language follows the app language by default; the toggle lets the user override per-session.
  const [voiceLang, setVoiceLang] = useState(() =>
    ((i18n.language || "en").split("-")[0].toLowerCase() === "fr") ? "fr-CA" : "en-US"
  );

  // Android/iOS Chrome mishandles continuous + interim results (stops early, duplicates).
  const isMobile = typeof navigator !== "undefined" && /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);

  useEffect(() => () => { wantListeningRef.current = false; try { recognitionRef.current?.stop(); } catch {} }, []);

  const buildRecognition = () => {
    const rec = new SpeechRecognition();
    rec.continuous = !isMobile;      // mobile Chrome ignores/breaks continuous — we restart manually
    rec.interimResults = !isMobile;  // interim results duplicate on Android — finals only on mobile
    rec.maxAlternatives = 1;
    rec.lang = voiceLang;

    rec.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        const txt = res[0].transcript;
        if (res.isFinal) {
          const seg = txt.trim();
          if (seg && seg !== lastFinalRef.current) {   // skip Android duplicate finals
            finalRef.current += seg + " ";
            lastFinalRef.current = seg;
          }
        } else {
          interim += txt;
        }
      }
      const combined = (baseTextRef.current + finalRef.current + interim).replace(/\s+/g, " ").trimStart();
      setForm((f) => ({ ...f, activity: combined }));
    };

    rec.onerror = (e) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        wantListeningRef.current = false;
        setVoiceError(t("dailyLog.voiceDenied"));
        setListening(false);
      } else if (e.error === "audio-capture") {
        wantListeningRef.current = false;
        setVoiceError(t("dailyLog.voiceNoMic"));
        setListening(false);
      } else if (e.error === "no-speech" || e.error === "aborted") {
        // transient — onend will restart if the user still wants to listen
      } else {
        if (!wantListeningRef.current) setVoiceError(t("dailyLog.voiceStopped"));
      }
    };

    rec.onend = () => {
      // Mobile ends after each phrase; restart to keep dictating until the user stops.
      if (wantListeningRef.current) {
        setTimeout(() => {
          if (!wantListeningRef.current) return;
          try { recognitionRef.current?.start(); } catch { setListening(false); }
        }, 150);
      } else {
        setListening(false);
      }
    };
    return rec;
  };

  const toggleRecord = () => {
    if (!speechSupported || readOnly) return;
    if (listening || wantListeningRef.current) {
      wantListeningRef.current = false;
      try { recognitionRef.current?.stop(); } catch {}
      setListening(false);
      return;
    }
    setVoiceError("");
    baseTextRef.current = form.activity ? form.activity.trimEnd() + " " : "";
    finalRef.current = "";
    lastFinalRef.current = "";
    const rec = buildRecognition();
    recognitionRef.current = rec;
    wantListeningRef.current = true;
    try { rec.start(); setListening(true); }
    catch { wantListeningRef.current = false; setListening(false); setVoiceError(t("dailyLog.voiceStartFail")); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error(t("dailyLog.fileTooLarge")); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/logs/extract-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachment({ name: res.data.filename, text: res.data.text });
      toast.success(t("dailyLog.attached", { name: res.data.filename }));
    } catch (err) {
      toast.error(apiError(err, t("dailyLog.fileError")));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const loadTodays = async () => {
    try {
      const res = await api.get("/logs", { params: { start_date: form.date, end_date: form.date } });
      setTodays(res.data);
    } catch { /* ignore */ }
  };
  const loadStreak = async () => {
    try {
      const res = await api.get("/logs/streak", { params: { local_date: localToday() } });
      setStreak(res.data);
      if (res.data.celebrate) {
        const m = res.data.celebrate;
        fireConfetti();
        toast.success(MILESTONE_MSG[m] || `${m}-day streak!`);
        try { await api.post("/logs/streak/ack-milestone", { local_date: localToday() }); } catch { /* ignore */ }
      }
    } catch { /* ignore */ }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadStreak(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadTodays(); }, [form.date]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });


  const submit = async (e) => {
    e.preventDefault();
    if (!form.activity.trim()) { toast.error(t("dailyLog.describeActivity")); return; }
    setLoading(true);
    try {
      await api.post("/logs", {
        activity: form.activity, date: form.date,
        hours: parseInt(form.hours) || 0, minutes: parseInt(form.minutes) || 0,
        linked_objective: form.linked_objective || null, notes: form.notes || null,
        attachment_name: attachment?.name || null, attachment_text: attachment?.text || null,
      });
      toast.success(t("dailyLog.activityLogged"));
      setForm({ ...form, activity: "", hours: "0", minutes: "0", linked_objective: "", notes: "" });
      setAttachment(null);
      loadTodays();
      loadStreak();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="animate-fade-up">
      <ReadOnlyBanner />
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold text-navy">{t("dailyLog.title")}</h1>
        <p className="text-slatebody mt-1">{t("dailyLog.subtitle")}</p>
        <p className="text-xs text-slatebody mt-3" data-testid="log-privacy-note">{t("dailyLog.privacyNoteShort")}</p>
        <p className="text-xs text-slatebody mt-1" data-testid="daily-log-trust-msg">{t("dailyLog.trustMsg")}</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <form onSubmit={submit} className="rounded-xl border border-slate-200 bg-white p-6 md:p-8 space-y-5" data-testid="daily-log-form">
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="activity">{t("dailyLog.activity")}</Label>
                <div className="flex items-center gap-2">
                  {speechSupported && (
                    <div className="inline-flex rounded-full bg-slate-100 p-0.5" data-testid="voice-lang-toggle">
                      {["en-US", "fr-CA"].map((lng) => (
                        <button key={lng} type="button" disabled={readOnly || listening}
                          onClick={() => setVoiceLang(lng)}
                          data-testid={`voice-lang-${lng === "fr-CA" ? "fr" : "en"}`}
                          className={`px-2 py-0.5 rounded-full text-[11px] font-medium transition-colors ${
                            voiceLang === lng ? "bg-white text-navy shadow-sm" : "text-slatebody hover:text-navy"
                          } ${(readOnly || listening) ? "opacity-50 cursor-not-allowed" : ""}`}
                          title={lng === "fr-CA" ? t("dailyLog.voiceLangFrTitle") : t("dailyLog.voiceLangEnTitle")}>
                          {lng === "fr-CA" ? t("dailyLog.voiceLangFr") : t("dailyLog.voiceLangEn")}
                        </button>
                      ))}
                    </div>
                  )}
                  <button type="button" onClick={toggleRecord} disabled={!speechSupported || readOnly}
                    data-testid="voice-record-btn"
                    title={speechSupported ? (listening ? t("dailyLog.stopRecording") : t("dailyLog.dictate")) : t("dailyLog.voiceUnsupportedTitle")}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                      !speechSupported || readOnly
                        ? "text-slate-400 bg-slate-100 cursor-not-allowed"
                        : listening
                          ? "text-white bg-red-500 hover:bg-red-600"
                          : "text-[#202123] bg-[#ECECEC]/10 hover:bg-[#E0E0E0]/20"
                    }`}>
                    {listening ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
                    {!speechSupported ? t("dailyLog.voiceNA") : listening ? t("dailyLog.recording") : t("dailyLog.record")}
                    {listening && <span className="h-2 w-2 rounded-full bg-white animate-pulse ml-0.5" />}
                  </button>
                </div>
              </div>
              <Textarea id="activity" value={form.activity} onChange={set("activity")} disabled={readOnly}
                rows={3} className="mt-1.5 resize-none" data-testid="log-activity"
                placeholder={t("dailyLog.activityPlaceholder")} />
              {!speechSupported && (
                <p className="text-xs text-slatebody mt-1.5" data-testid="voice-unsupported-msg">{t("dailyLog.voiceUnsupported")}</p>
              )}
              {voiceError && (
                <p className="text-xs text-red-600 mt-1.5" data-testid="voice-error-msg">{voiceError}</p>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-4 items-start">
              <div>
                <Label htmlFor="date" className="flex items-center h-5 leading-none">{t("common.date")}</Label>
                <Input id="date" type="date" value={form.date} onChange={set("date")} disabled={readOnly}
                  className="mt-1.5 font-mono" data-testid="log-date" />
              </div>
              <div>
                <Label className="flex items-center gap-1.5 h-5 leading-none whitespace-nowrap"><Clock className="h-3.5 w-3.5 shrink-0" /> {t("dailyLog.timeSpent")}</Label>
                <div className="mt-1.5 flex items-center gap-2">
                  <Input type="number" min="0" value={form.hours} onChange={set("hours")} disabled={readOnly}
                    className="font-mono" data-testid="log-hours" />
                  <span className="text-slatebody text-sm">h</span>
                  <Input type="number" min="0" max="59" value={form.minutes} onChange={set("minutes")} disabled={readOnly}
                    className="font-mono" data-testid="log-minutes" />
                  <span className="text-slatebody text-sm">m</span>
                </div>
              </div>
            </div>

            <div>
              <Label>{t("dailyLog.attachment")} <span className="text-slatebody font-normal">{t("dailyLog.attachmentHint")}</span></Label>
              <input ref={fileRef} type="file" accept=".txt,.md,.csv,.pdf,.docx,.xlsx" onChange={onFile}
                className="hidden" data-testid="log-attachment-input" />
              {!attachment ? (
                <Button type="button" variant="outline" disabled={readOnly || uploading}
                  onClick={() => fileRef.current?.click()} className="mt-1.5 gap-2" data-testid="log-attach-btn">
                  <Paperclip className="h-4 w-4" /> {uploading ? t("dailyLog.reading") : t("dailyLog.attachDoc")}
                </Button>
              ) : (
                <div className="mt-1.5 flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2" data-testid="log-attachment-chip">
                  <span className="flex items-center gap-2 text-sm text-navy min-w-0">
                    <FileText className="h-4 w-4 text-[#202123] shrink-0" />
                    <span className="truncate">{attachment.name}</span>
                  </span>
                  <button type="button" onClick={() => setAttachment(null)} data-testid="log-attachment-remove"
                    className="text-slatebody hover:text-red-600 shrink-0"><X className="h-4 w-4" /></button>
                </div>
              )}
              <p className="text-xs text-slatebody mt-1.5">{t("dailyLog.attachmentExtractNote")}</p>
            </div>

            <Button type="submit" disabled={loading || readOnly} className="bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0] gap-2" data-testid="log-submit">
              <PenLine className="h-4 w-4" /> {loading ? t("dailyLog.logging") : t("dailyLog.logActivity")}
            </Button>
          </form>
        </div>

        <div>
          {streak && streak.current > 0 && (
            <div className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-6 mb-6" data-testid="streak-card">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-amber-100">
                  <Flame className="h-6 w-6 text-orange-500" />
                </div>
                <div>
                  <p className="font-display text-2xl font-bold text-navy leading-none" data-testid="streak-count">
                    {streak.current}<span className="text-base font-semibold text-slatebody"> {t("dailyLog.streakDays", { count: streak.current })}</span>
                  </p>
                  <p className="text-xs text-slatebody mt-1">
                    {streak.logged_today ? t("dailyLog.streakRoll") : t("dailyLog.streakLogToday")}
                  </p>
                </div>
              </div>
              {(streak.freeze_available || streak.used_freeze) && (
                <div className="flex items-center gap-1.5 mt-3" data-testid="streak-freeze">
                  <Snowflake className="h-3 w-3 text-sky-400" />
                  <span className="text-[11px] text-slatebody/80">
                    {streak.used_freeze ? t("dailyLog.freezeUsedSubtle") : t("dailyLog.freezeReadySubtle")}
                  </span>
                </div>
              )}
            </div>
          )}
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <h3 className="font-display font-semibold text-navy mb-1">{t("dailyLog.loggedOn")} <span className="font-mono text-sm">{form.date}</span></h3>
            <p className="text-xs text-slatebody mb-4">{t("dailyLog.entries", { count: todays.length })}</p>
            <div className="space-y-3" data-testid="todays-entries">
              {todays.length === 0 && <p className="text-sm text-slatebody">{t("dailyLog.nothingLogged")}</p>}
              {todays.map((e) => (
                <div key={e.id} className="rounded-md border border-slate-100 bg-background p-3">
                  <p className="text-sm text-navy leading-snug">{e.activity}</p>
                  {((e.hours || 0) > 0 || (e.minutes || 0) > 0 || e.linked_objective) && (
                    <p className="font-mono text-[11px] text-slatebody mt-1.5">
                      {((e.hours || 0) > 0 || (e.minutes || 0) > 0) ? `${e.hours || 0}h ${e.minutes || 0}m` : ""}
                      {e.linked_objective ? `${((e.hours || 0) > 0 || (e.minutes || 0) > 0) ? " · " : ""}${e.linked_objective}` : ""}
                    </p>
                  )}
                  {e.attachment_name && (
                    <p className="flex items-center gap-1.5 text-[11px] text-[#202123] mt-1.5" data-testid="entry-attachment">
                      <Paperclip className="h-3 w-3" /> {e.attachment_name}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
